import type { ParitaWorkspace } from "@/common/user";
import { AxisGroup, MeasureV2 } from "@/common/measures";

function formatCurrency(value: number | string): string {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumSignificantDigits: 4,
  }).format(numberValue);
}

export function formatPercentage(value: number | string): string {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  return `${parseFloat(numberValue.toFixed(2))}%`;
}

export function formatFractionPercentage(value: number | string): string {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  return formatPercentage(numberValue * 100);
}

export function formatNumber(value: number | string): string {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    maximumSignificantDigits: 4,
    notation: "compact",
  }).format(numberValue);
}

export function formatNumberLong(value: number | string): string {
  const numberValue = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    maximumSignificantDigits: 6,
  }).format(numberValue);
}

export const formattersByAxisGroup: Record<
  AxisGroup,
  (
    value: number | string,
    measure: MeasureV2,
    workspace?: ParitaWorkspace,
  ) => string
> = {
  actions_taken: formatNumber,
  applicant_count: formatNumber,
  avg_compensation: formatCurrency,
  benchmark_percentage: formatPercentage,
  color_energy: formatNumber,
  company_count: formatNumber,
  compensation_gap: formatPercentage,
  cost_of_attrition: formatCurrency,
  fraction_percentage: formatFractionPercentage,
  generic_percentage: formatPercentage,
  goal_count: formatNumber,
  headcount: formatNumber,
  hires: formatNumber,
  importance: formatNumber,
  incident_count: formatNumber,
  intensity: formatNumber,
  intensity10: formatNumber,
  job_req_count: formatNumber,
  market_count: formatNumber,
  number_of_respondents: formatNumber,
  performance_rating: formatNumber,
  probability: formatNumber,
  promotions: formatNumber,
  survey_score: formatNumber,
  terminations: formatNumber,
  time_in_days: formatNumber,
  total_compensation: formatCurrency,
};

export const formatValueV2 = (
  value: number | string,
  measure: MeasureV2,
  workspace?: ParitaWorkspace,
): string => {
  const formatter = formattersByAxisGroup[measure.axisGroup];
  if (!formatter) {
    console.warn(`No formatter found for axis group: ${measure.axisGroup}`);
    return String(value);
  }
  try {
    return formatter(value, measure, workspace);
  } catch (error) {
    console.error("Error formatting value:", error);
    return String(value);
  }
};
